import { HOUSEHOLD_DOC, MEMBER_COLORS, type HouseholdMember } from '@awwyeah/shared';
import { useYMap, useYDoc } from '../../sync';
import { useUser } from '../auth';

/**
 * Who's who, for avatars, the archive's per-member filter and the "Seen by" row.
 *
 * A member's colour is their own stored profile hex (household-doc `color`), applied
 * inline — not one of the four `--m-faye/dan/ezry/kora` design tokens assigned by roster
 * position. That slot scheme is what usability testing (2026-08-01) caught rendering two
 * different members near-identically; see docs/DECISIONS.md. Anyone not in the roster yet
 * gets a deterministic fallback hex (stable hash of their id) rather than borrowing a slot.
 *
 * This module only ever **reads** the `household` doc; that module belongs to another
 * workstream.
 */

/** Deterministic fallback colour for a member id we have no profile for yet — same
 * approach as `lists/useHouseholdRoster.ts`, so an unsynced member looks the same
 * everywhere rather than picking a different placeholder colour per surface. */
function fallbackColor(id: string): string {
  let hash = 0;
  for (let index = 0; index < id.length; index += 1)
    hash = (hash * 31 + id.charCodeAt(index)) >>> 0;
  return MEMBER_COLORS[hash % MEMBER_COLORS.length] as string;
}

export interface CheckinMember {
  id: string;
  name: string;
  initial: string;
  /** The member's own profile hex, or a deterministic fallback if unsynced. */
  color: string;
  /** A member's chosen glyph replaces their initial when they have one. */
  avatarEmoji?: string;
  /** No profile on this device yet — rendered as the design's neutral "?" avatar. */
  unknown?: boolean;
}

export function initialOf(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase();
}

/** Builds the directory. Pure, so it can be tested directly. */
export function buildMemberDirectory(
  roster: HouseholdMember[],
  self?: { id: string; name: string; color?: string; avatarEmoji?: string } | null,
): Map<string, CheckinMember> {
  const directory = new Map<string, CheckinMember>();

  for (const member of roster) {
    directory.set(member.id, {
      id: member.id,
      name: member.name,
      initial: initialOf(member.name),
      color: member.color,
      ...(member.avatarEmoji ? { avatarEmoji: member.avatarEmoji } : {}),
    });
  }

  // The signed-in member is always known, roster or not.
  if (self && !directory.has(self.id)) {
    directory.set(self.id, {
      id: self.id,
      name: self.name,
      initial: initialOf(self.name),
      color: self.color ?? fallbackColor(self.id),
      ...(self.avatarEmoji ? { avatarEmoji: self.avatarEmoji } : {}),
    });
  }

  return directory;
}

/**
 * Anyone we have no profile for yet — the `household` roster is another workstream's to
 * fill, and until it exists only the signed-in member is nameable. Rendered as the design
 * package's explicit "no profile" avatar (MemberChip: dashed outline, neutral fill, `?`)
 * rather than a coloured circle that would imply we know who this is.
 */
export function unknownMember(id: string): CheckinMember {
  return { id, name: 'Someone', initial: '?', color: fallbackColor(id), unknown: true };
}

export interface MemberDirectory {
  get(id: string): CheckinMember;
  all(): CheckinMember[];
  selfId: string | null;
}

export function useMembers(): MemberDirectory {
  const { user } = useUser();
  const householdDoc = useYDoc(HOUSEHOLD_DOC);
  const { entries } = useYMap<HouseholdMember>(householdDoc, 'members');

  const roster = entries.map(([, member]) => member).filter((member) => Boolean(member?.id));
  const directory = buildMemberDirectory(
    roster,
    user
      ? {
          id: user.id,
          name: user.name,
          color: user.color,
          ...(user.avatarEmoji ? { avatarEmoji: user.avatarEmoji } : {}),
        }
      : null,
  );

  return {
    get: (id) => directory.get(id) ?? unknownMember(id),
    all: () => [...directory.values()],
    selfId: user?.id ?? null,
  };
}
